/**
 * Help.jsx — Knowledge-base page at `/help`.
 *
 * - Role-filtered list of how-tos (data fetched from /api/help/articles)
 * - Search box (live filter across title + summary + steps)
 * - Audience-filter pills (All / Writers / Admins)
 * - Each card collapsible, with a "Mark as read" tick that calls
 *   POST /api/help/articles/{id}/read and persists across devices
 * - Empty state when search/filter combo yields nothing
 *
 * Keeps the dark slate aesthetic to blend with the rest of the app.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  ChevronDown, ChevronUp, Search, Check, BookOpenCheck, Loader2,
  Sparkles, Lightbulb,
} from 'lucide-react';
import { authFetch } from '../utils/authUtils';
import { IconFor, renderInline, audienceMeta } from '../utils/helpUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const Help = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [query, setQuery] = useState('');
  const [audFilter, setAudFilter] = useState('all'); // all | writer | admin

  const refresh = async () => {
    try {
      const r = await authFetch(`${BACKEND_URL}/api/help/articles`);
      if (r.ok) {
        const d = await r.json();
        setArticles(d.articles || []);
      }
    } catch { /* non-fatal */ }
    finally { setLoading(false); }
  };

  useEffect(() => { refresh(); }, []);

  const markRead = async (id) => {
    setArticles((prev) => prev.map((a) => (a.id === id ? { ...a, read: true } : a)));
    try {
      await authFetch(`${BACKEND_URL}/api/help/articles/${id}/read`, { method: 'POST' });
    } catch { /* the optimistic flip stays even if the server hiccups */ }
  };

  const filtered = useMemo(() => {
    let list = articles;
    if (audFilter !== 'all') list = list.filter((a) => a.audience === audFilter);
    const q = query.trim().toLowerCase();
    if (q) {
      list = list.filter((a) => {
        const hay = [
          a.title, a.summary, ...(a.steps || []), a.pro_tip || '',
        ].join(' ').toLowerCase();
        return hay.includes(q);
      });
    }
    return list;
  }, [articles, audFilter, query]);

  const readCount = articles.filter((a) => a.read).length;

  const showWriterFilter = articles.some((a) => a.audience === 'writer');
  const showAdminFilter = articles.some((a) => a.audience === 'admin');

  return (
    <div className="min-h-screen bg-[#0b0a14] text-white pb-24" data-testid="help-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-8">
        <header className="mb-6">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] font-bold text-indigo-300 mb-2">
            <BookOpenCheck className="w-3.5 h-3.5" />
            Help Center
          </div>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight">
            How-tos & quick guides
          </h1>
          <p className="text-sm text-slate-400 mt-1.5">
            Short, practical walkthroughs for everything you'll do on the platform.
            {articles.length > 0 && (
              <>
                {' · '}
                <span className="text-indigo-300 font-bold tabular-nums">
                  {readCount}/{articles.length}
                </span>
                {' '}read
              </>
            )}
          </p>
        </header>

        {/* Search + filter pills */}
        <div className="mb-5 space-y-2.5">
          <label className="relative block">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search how-tos…"
              className="w-full pl-10 pr-3 py-2.5 rounded-lg bg-slate-900 border border-slate-700 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/40"
              data-testid="help-search"
            />
          </label>

          {(showWriterFilter || showAdminFilter) && (
            <div className="flex items-center gap-1.5 flex-wrap" role="tablist">
              {[
                { id: 'all',    label: 'All' },
                showWriterFilter && { id: 'writer', label: 'Scribes' },
                showAdminFilter  && { id: 'admin',  label: 'Admins' },
              ].filter(Boolean).map((f) => (
                <button
                  key={f.id}
                  type="button"
                  role="tab"
                  aria-selected={audFilter === f.id}
                  onClick={() => setAudFilter(f.id)}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold border transition focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-300 ${
                    audFilter === f.id
                      ? 'bg-indigo-500/25 border-indigo-300 text-white'
                      : 'bg-slate-800/60 border-slate-700 text-slate-300 hover:border-indigo-400/40'
                  }`}
                  data-testid={`help-filter-${f.id}`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Articles list */}
        {loading ? (
          <div className="py-14 text-center text-slate-400 inline-flex items-center gap-2 w-full justify-center">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading guides…
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-14 text-center text-slate-500 text-sm" data-testid="help-empty">
            No how-tos match — try a different search or filter.
          </div>
        ) : (
          <ul className="space-y-2.5" data-testid="help-list">
            {filtered.map((a) => {
              const open = openId === a.id;
              const aud = audienceMeta(a.audience);
              return (
                <li
                  key={a.id}
                  className={`rounded-xl border bg-slate-900/60 overflow-hidden transition ${
                    open ? 'border-indigo-500/50' : 'border-slate-800 hover:border-slate-700'
                  }`}
                  data-testid={`help-card-${a.id}`}
                >
                  <button
                    type="button"
                    onClick={() => setOpenId(open ? null : a.id)}
                    className="w-full text-left flex items-start gap-3 p-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-300 rounded-xl"
                    aria-expanded={open}
                  >
                    <div className="shrink-0 w-10 h-10 rounded-lg bg-indigo-500/15 border border-indigo-500/30 flex items-center justify-center">
                      <IconFor name={a.icon} className="w-5 h-5 text-indigo-200" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start gap-2 flex-wrap">
                        <h2 className="text-sm sm:text-base font-bold text-white">{a.title}</h2>
                        <span className={`shrink-0 inline-flex items-center text-[10px] uppercase tracking-widest font-black px-1.5 py-0.5 rounded border ${aud.c}`}>
                          {aud.label}
                        </span>
                        {a.read && (
                          <span className="shrink-0 inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-emerald-300">
                            <Check className="w-3 h-3" /> Read
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-slate-400 mt-0.5">{a.summary}</p>
                    </div>
                    <span className="shrink-0 w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center text-slate-300">
                      {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </span>
                  </button>

                  {open && (
                    <div className="px-4 pb-4 -mt-1 space-y-3" data-testid={`help-body-${a.id}`}>
                      <ol className="space-y-2 pl-1">
                        {(a.steps || []).map((step, idx) => (
                          <li key={idx} className="flex gap-3">
                            <span className="shrink-0 w-6 h-6 rounded-full bg-indigo-500/25 text-indigo-100 text-[11px] font-black tabular-nums flex items-center justify-center">
                              {idx + 1}
                            </span>
                            <span className="text-sm text-slate-200 leading-relaxed">{renderInline(step)}</span>
                          </li>
                        ))}
                      </ol>

                      {a.pro_tip && (
                        <div className="flex items-start gap-2 rounded-lg bg-amber-500/10 border border-amber-500/30 p-3">
                          <Lightbulb className="w-4 h-4 text-amber-300 shrink-0 mt-0.5" />
                          <p className="text-xs text-amber-100 leading-relaxed">
                            <strong className="font-bold uppercase tracking-widest text-[10px] text-amber-300 mr-1.5">Pro tip</strong>
                            {renderInline(a.pro_tip)}
                          </p>
                        </div>
                      )}

                      <div className="flex justify-end pt-1">
                        <button
                          type="button"
                          onClick={() => markRead(a.id)}
                          disabled={a.read}
                          className={`inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-lg border transition focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300 ${
                            a.read
                              ? 'bg-emerald-500/15 border-emerald-500/30 text-emerald-200 cursor-default'
                              : 'bg-emerald-600 hover:bg-emerald-500 text-white border-emerald-500'
                          }`}
                          data-testid={`help-mark-read-${a.id}`}
                        >
                          {a.read ? <><Check className="w-3.5 h-3.5" /> Marked as read</> : <><Sparkles className="w-3.5 h-3.5" /> Mark as read</>}
                        </button>
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default Help;
